/**
 * ENG-6171 — wedge-respawn poison-task guard.
 *
 * With ENG-6153 wedge enforce armed, a wedged agent is force-fresh respawned
 * and then nudged back onto its in-progress kanban card (the hybrid inject —
 * ENG-5435 — treats `in_progress` as actionable). That's the desired behaviour
 * EXCEPT when the in-progress card is *what caused the wedge* (e.g. a tool call
 * that hangs the Stop hook). Then the agent loops:
 *
 *   nudge → resume card → wedge → enforce respawn → nudge → resume card → wedge → …
 *
 * bounded only by the 60s hybrid debounce and the 30-min stale-auto-fail, but
 * still a respawn loop on a genuine poison task.
 *
 * This module is the pure, injectable core of the guard: it tracks a
 * per-(agent,card) *consecutive* wedge-respawn count and decides when a card
 * has earned the "suspected poison" label. The manager poll loop owns the
 * per-agent state map, the board snapshots, and the actual suppression — it
 * stops re-nudging a poisoned card (letting it fall to the 30-min
 * stale-auto-fail) while still nudging the agent's other actionable cards.
 *
 * Mirrors lib/wedge-detection.ts (pure predicate, caller-owned state) so the
 * decision is unit-testable with no I/O.
 *
 * Attribution semantics:
 *   - A wedge-respawn increments the count for every card that was in_progress
 *     at wedge time. A card not in the in-progress set at a given wedge has its
 *     streak BROKEN (dropped) — "consecutive" means the same card kept being
 *     the one in flight across successive wedges, not merely cumulative.
 *   - An *unattributable* wedge (no in_progress card determinable — e.g. an
 *     empty/never-fetched board cache) neither accumulates nor resets: it's a
 *     no-op, so a transient empty read can't falsely clear a real poison streak.
 *   - A card resets to zero when it leaves in_progress (board observation) or
 *     after a cooldown with no further attributable wedge (`pruneCardStates`).
 */

export interface PoisonCardConfig {
  /** Consecutive wedge-respawns attributable to a card before it's suppressed. */
  threshold: number;
  /** Reset a card's streak if no attributable wedge lands within this window (ms). */
  cooldownMs: number;
}

/** Per-card wedge-respawn accounting held by the caller (one map per agent). */
export interface CardWedgeState {
  /** Consecutive wedge-respawns attributed to this card. */
  count: number;
  /** Epoch ms of the most recent attributable wedge-respawn. */
  lastWedgeAtMs: number;
}

const DEFAULTS = {
  threshold: 3,
  cooldownSeconds: 1800, // 30 min — matches the kanban stale-auto-fail window
} as const;

function parsePositiveInt(raw: string | undefined, fallback: number, floor: number): number {
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isInteger(n) && n >= floor ? n : fallback;
}

/**
 * Resolve the poison-card config from the environment.
 *   - `AGT_WEDGE_POISON_CARD_THRESHOLD` (default 3, floor 2 — a single respawn
 *     must never suppress; hysteresis mirrors wedge-detection's minCycles).
 *   - `AGT_WEDGE_POISON_CARD_COOLDOWN_SECONDS` (default 1800, floor 300).
 */
export function resolvePoisonCardConfig(env: NodeJS.ProcessEnv = process.env): PoisonCardConfig {
  return {
    threshold: parsePositiveInt(env.AGT_WEDGE_POISON_CARD_THRESHOLD, DEFAULTS.threshold, 2),
    cooldownMs:
      parsePositiveInt(
        env.AGT_WEDGE_POISON_CARD_COOLDOWN_SECONDS,
        DEFAULTS.cooldownSeconds,
        300,
      ) * 1000,
  };
}

/**
 * Record a wedge-respawn, attributing it to the cards that were in_progress at
 * wedge time. Returns a NEW state map (the input is not mutated) plus the ids
 * of cards whose count crosses `threshold` on THIS respawn (so the caller logs
 * the "suspected poison" signal exactly once per episode).
 *
 * Cards present in `inProgressCardIds` are incremented; cards previously tracked
 * but absent now are dropped (their consecutive streak is broken). An empty
 * `inProgressCardIds` is treated as unattributable — the state is returned
 * unchanged (no accumulation, no reset).
 */
export function recordWedgeForCards(
  states: ReadonlyMap<string, CardWedgeState>,
  inProgressCardIds: readonly string[],
  nowMs: number,
  config: PoisonCardConfig,
): { next: Map<string, CardWedgeState>; newlyPoisoned: string[] } {
  if (inProgressCardIds.length === 0) {
    return { next: new Map(states), newlyPoisoned: [] };
  }
  const next = new Map<string, CardWedgeState>();
  const newlyPoisoned: string[] = [];
  for (const id of inProgressCardIds) {
    if (next.has(id)) continue; // de-dupe a board that lists the same id twice
    const count = (states.get(id)?.count ?? 0) + 1;
    next.set(id, { count, lastWedgeAtMs: nowMs });
    if (count === config.threshold) newlyPoisoned.push(id);
  }
  // Cards not in_progress at this wedge have their streak broken (dropped).
  return { next, newlyPoisoned };
}

/**
 * Reset accounting that no longer applies, against a fresh board observation:
 *   - a card no longer in_progress (the agent moved/closed it) — AC: "resets
 *     when the card leaves in_progress";
 *   - a card whose last attributable wedge is older than the cooldown — AC:
 *     "or after a cooldown".
 * Returns a NEW map (input not mutated).
 */
export function pruneCardStates(
  states: ReadonlyMap<string, CardWedgeState>,
  liveInProgressCardIds: Iterable<string>,
  nowMs: number,
  config: PoisonCardConfig,
): Map<string, CardWedgeState> {
  const live = liveInProgressCardIds instanceof Set ? liveInProgressCardIds : new Set(liveInProgressCardIds);
  const next = new Map<string, CardWedgeState>();
  for (const [id, state] of states) {
    if (!live.has(id)) continue; // left in_progress
    if (nowMs - state.lastWedgeAtMs > config.cooldownMs) continue; // cooled down
    next.set(id, state);
  }
  return next;
}

/** A card is a suspected poison task once its consecutive count meets the threshold. */
export function isCardPoisoned(
  states: ReadonlyMap<string, CardWedgeState>,
  cardId: string,
  config: PoisonCardConfig,
): boolean {
  return (states.get(cardId)?.count ?? 0) >= config.threshold;
}

/**
 * Split actionable cards into those the manager may still nudge (`allowed`) and
 * those suppressed as suspected poison tasks (`suppressed`). Order is preserved.
 */
export function partitionActionableByPoison<T extends { id: string }>(
  actionable: readonly T[],
  states: ReadonlyMap<string, CardWedgeState>,
  config: PoisonCardConfig,
): { allowed: T[]; suppressed: T[] } {
  const allowed: T[] = [];
  const suppressed: T[] = [];
  for (const item of actionable) {
    if (isCardPoisoned(states, item.id, config)) suppressed.push(item);
    else allowed.push(item);
  }
  return { allowed, suppressed };
}
